/**
 * Line icons — 24×24, currentColor stroke, 1.8 default weight.
 *
 * Ported from src/frontend/components/components-mobile/ui.jsx so the
 * web shares the same icon vocabulary as the mobile design files. Use
 * sparingly + consistently — every icon should pull from this set so a
 * future bulk restyle (eg. switching to filled, or 2x stroke) is one
 * file change away.
 */
import { CSSProperties, ReactNode } from 'react';

type IconProps = {
  size?: number;
  stroke?: number;
  fill?: string;
  style?: CSSProperties;
  className?: string;
  'aria-label'?: string;
};

function Svg({
  size = 22, stroke = 1.8, fill = 'none', style, className,
  children, label,
}: IconProps & { children: ReactNode; label?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      role={label ? 'img' : 'presentation'}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {children}
    </svg>
  );
}

export const Icons = {
  heart:  (p: IconProps) => <Svg {...p}><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></Svg>,
  x:      (p: IconProps) => <Svg {...p}><path d="M18 6L6 18M6 6l12 12"/></Svg>,
  check:  (p: IconProps) => <Svg {...p}><path d="M20 6L9 17l-5-5"/></Svg>,
  chevR:  (p: IconProps) => <Svg {...p}><path d="M9 18l6-6-6-6"/></Svg>,
  chevL:  (p: IconProps) => <Svg {...p}><path d="M15 18l-6-6 6-6"/></Svg>,
  chevU:  (p: IconProps) => <Svg {...p}><path d="M18 15l-6-6-6 6"/></Svg>,
  chevD:  (p: IconProps) => <Svg {...p}><path d="M6 9l6 6 6-6"/></Svg>,
  home:   (p: IconProps) => <Svg {...p}><path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1h-5v-7h-6v7H4a1 1 0 01-1-1v-9.5z"/></Svg>,
  feed:   (p: IconProps) => <Svg {...p}><rect x="3" y="3" width="7" height="9" rx="2"/><rect x="14" y="3" width="7" height="5" rx="2"/><rect x="14" y="12" width="7" height="9" rx="2"/><rect x="3" y="16" width="7" height="5" rx="2"/></Svg>,
  spark:  (p: IconProps) => <Svg {...p}><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/></Svg>,
  chat:   (p: IconProps) => <Svg {...p}><path d="M21 15a2 2 0 01-2 2H8l-5 4V5a2 2 0 012-2h14a2 2 0 012 2v10z"/></Svg>,
  user:   (p: IconProps) => <Svg {...p}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0116 0"/></Svg>,
  share:  (p: IconProps) => <Svg {...p}><path d="M12 3v13M8 7l4-4 4 4"/><path d="M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"/></Svg>,
  plus:   (p: IconProps) => <Svg {...p}><path d="M12 5v14M5 12h14"/></Svg>,
  camera: (p: IconProps) => <Svg {...p}><path d="M4 7h3l2-3h6l2 3h3a1 1 0 011 1v11a1 1 0 01-1 1H4a1 1 0 01-1-1V8a1 1 0 011-1z"/><circle cx="12" cy="13" r="4"/></Svg>,
  bell:   (p: IconProps) => <Svg {...p}><path d="M18 16V11a6 6 0 10-12 0v5l-2 3h16l-2-3zM10 21a2 2 0 004 0"/></Svg>,
  lock:   (p: IconProps) => <Svg {...p}><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/></Svg>,
  music:  (p: IconProps) => <Svg {...p}><path d="M9 17V5l12-2v12"/><circle cx="6" cy="17" r="3"/><circle cx="18" cy="15" r="3"/></Svg>,
  info:   (p: IconProps) => <Svg {...p}><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v5h1"/></Svg>,
  send:   (p: IconProps) => <Svg {...p}><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></Svg>,
  clock:  (p: IconProps) => <Svg {...p}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></Svg>,
  copy:   (p: IconProps) => <Svg {...p}><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M4 16V6a2 2 0 012-2h10"/></Svg>,
  edit:   (p: IconProps) => <Svg {...p}><path d="M17 3l4 4-13 13H4v-4L17 3z"/></Svg>,
  eye:    (p: IconProps) => <Svg {...p}><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></Svg>,
  flag:   (p: IconProps) => <Svg {...p}><path d="M4 21V4"/><path d="M4 4h12l-2 4 2 4H4"/></Svg>,
  search: (p: IconProps) => <Svg {...p}><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></Svg>,
  dots:   (p: IconProps) => <Svg {...p}><circle cx="5" cy="12" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="19" cy="12" r="1.2"/></Svg>,
  arrow:  (p: IconProps) => <Svg {...p}><path d="M5 12h14M13 5l7 7-7 7"/></Svg>,
  star:   (p: IconProps) => <Svg {...p}><path d="M12 2l3 6.5 7 1-5 4.9 1.2 7.1L12 18l-6.2 3.5L7 14.4 2 9.5l7-1L12 2z"/></Svg>,
  gear:   (p: IconProps) => <Svg {...p}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></Svg>,
  signout: (p: IconProps) => <Svg {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/></Svg>,
};

export type IconKey = keyof typeof Icons;
